import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

/**
 * A row-shape spec for a Custom Layout template. The dashboard's existing
 * widgets are flattened and re-bucketed into rows of the given sizes.
 *
 * Example: rowCounts [2, 3, 1] → row 1 holds 2 widgets, row 2 holds 3,
 * row 3 holds 1. The underlying widgets are taken from the dashboard's own
 * config in document order.
 */
export interface LayoutPreset {
  id: string;
  name: string;
  rowCounts: number[];
}

export interface LayoutManifest {
  layouts: LayoutPreset[];
}

/**
 * Legacy storage key from when layout was a single global setting. Still read
 * (never written) so users who picked a layout before per-dashboard scoping
 * shipped continue to see it as the default for dashboards they haven't
 * touched yet. Cleared by `clearAllSavedLayouts()` (Reset to Default).
 */
const STORAGE_KEY_LEGACY_GLOBAL = 'dvm.layout.activeId';

/**
 * Per-dashboard saved layouts: `{ <dashboardKey>: <layoutId | null> }`.
 *   - string value: user explicitly picked this layout for that dashboard
 *   - null value:   user explicitly cleared the layout (suppresses any JSON
 *                   default and the legacy global fallback)
 *   - missing key:  user has never touched this dashboard's layout — falls
 *                   back to dashboard JSON `layoutId` then to the legacy global
 */
const STORAGE_KEY_PER_DASHBOARD = 'dvm.layout.perDashboard';

@Injectable({ providedIn: 'root' })
export class LayoutService {
  private manifestCache: LayoutManifest | null = null;

  private manifestSubject = new BehaviorSubject<LayoutManifest | null>(null);
  manifest$ = this.manifestSubject.asObservable();

  /**
   * Active layout id for the *currently-viewed dashboard*, or `null` for
   * "use that dashboard's own pageContent". Updated whenever:
   *   1. a dashboard registers itself via `setActiveDashboardKey()`
   *   2. the user previews/commits/clears a layout via the Theme Options dialog
   */
  private activeLayoutIdSubject = new BehaviorSubject<string | null>(null);
  activeLayoutId$ = this.activeLayoutIdSubject.asObservable();

  /**
   * The last layout id that was *persisted* for the active dashboard (the
   * saved baseline). The "active" id may diverge during preview before the
   * user commits via the toolbar Save button.
   */
  private savedLayoutIdForActive: string | null = null;

  /**
   * True while a previewed layout is live but not yet saved. The toolbar
   * "Save Theme" button subscribes (alongside ThemeService.pendingPreview$)
   * so a single Save commits both pending theme AND pending layout.
   */
  private pendingLayoutSubject = new BehaviorSubject<boolean>(false);
  pendingLayout$ = this.pendingLayoutSubject.asObservable();

  /**
   * Per-dashboard saved-layout map, hydrated from localStorage. Mutated on
   * commit/clear, then rewritten back to storage in one shot.
   */
  private perDashboardSaved: Record<string, string | null> = {};

  /**
   * Legacy single-global layout from before per-dashboard scoping. Read once
   * at boot; falls through as the default for any dashboard whose JSON
   * doesn't declare its own `layoutId` and that has no per-dashboard entry.
   */
  private legacyGlobalLayoutId: string | null = null;

  /**
   * Key of the dashboard currently in view (e.g. its config filename or
   * dvmWorkflowAppId). `null` until a dashboard registers via
   * `setActiveDashboardKey()`. All preview/commit/clear ops are scoped to
   * this dashboard.
   */
  private activeDashboardKey: string | null = null;

  constructor(private http: HttpClient) {
    this.perDashboardSaved = this.readPerDashboardMap();
    this.legacyGlobalLayoutId = this.readLegacyGlobal();
  }

  /** Load the manifest JSON listing all layout presets. */
  loadManifest(): Observable<LayoutManifest> {
    if (this.manifestCache) return of(this.manifestCache);
    return this.http
      .get<LayoutManifest>('assets/layout-config/layouts-manifest.json')
      .pipe(
        tap((m) => {
          this.manifestCache = m;
          this.manifestSubject.next(m);
        }),
        catchError(() => {
          const empty: LayoutManifest = { layouts: [] };
          this.manifestCache = empty;
          this.manifestSubject.next(empty);
          return of(empty);
        }),
      );
  }

  getManifest(): LayoutManifest | null {
    return this.manifestCache;
  }

  /** Find a layout by id from the loaded manifest. */
  getLayout(id: string | null): LayoutPreset | null {
    if (!id || !this.manifestCache) return null;
    return this.manifestCache.layouts.find((l) => l.id === id) || null;
  }

  /** Currently-active layout id (in-memory). */
  getActiveLayoutId(): string | null {
    return this.activeLayoutIdSubject.value;
  }

  /**
   * Switch the service to a different dashboard. Resolves which layout
   * applies to it (per-dashboard saved → dashboard JSON default → legacy
   * global) and emits the resolved id on `activeLayoutId$`.
   *
   * Call once per dashboard load — after the config arrives, before
   * `applyDashboardWithLayout`. Resets any in-flight preview state because
   * a half-applied preview from another dashboard shouldn't leak across.
   */
  setActiveDashboardKey(
    key: string | null,
    dashboardDefault: string | null,
  ): void {
    this.activeDashboardKey = key;
    const resolved = this.resolveLayoutIdFor(key, dashboardDefault);
    this.savedLayoutIdForActive = resolved;
    this.pendingLayoutSubject.next(false);
    this.activeLayoutIdSubject.next(resolved);
  }

  /**
   * Apply a layout selection live (no persistence). Sets the pending flag so
   * the toolbar Save button surfaces; the user commits via commitPendingLayout().
   * Mirrors ThemeService.applyTemplatePreview / applyCustomPresetPreview.
   */
  applyLayoutPreview(id: string | null): void {
    this.activeLayoutIdSubject.next(id);
    this.pendingLayoutSubject.next(id !== this.savedLayoutIdForActive);
  }

  /**
   * Apply + persist a layout selection in one step. Used for destructive
   * intents like "Clear Layout" where preview-then-save would be friction.
   */
  setActiveLayoutId(id: string | null): void {
    this.persistForActiveDashboard(id);
    this.savedLayoutIdForActive = id;
    this.activeLayoutIdSubject.next(id);
    this.pendingLayoutSubject.next(false);
  }

  /** Persist the currently-previewed layout id under the active dashboard's
   *  key. Called by the toolbar Save button alongside
   *  ThemeService.commitPendingPreview. */
  commitPendingLayout(): void {
    if (!this.pendingLayoutSubject.value) return;
    const id = this.activeLayoutIdSubject.value;
    this.persistForActiveDashboard(id);
    this.savedLayoutIdForActive = id;
    this.pendingLayoutSubject.next(false);
  }

  /** Drop any in-flight preview and revert to the last-saved layout. */
  discardPendingLayout(): void {
    if (!this.pendingLayoutSubject.value) return;
    this.activeLayoutIdSubject.next(this.savedLayoutIdForActive);
    this.pendingLayoutSubject.next(false);
  }

  /**
   * Drop the layout for the *currently-active dashboard*. Persists an
   * explicit-null entry under that dashboard's key so the choice sticks
   * across reloads — without this, a JSON `layoutId` default would
   * silently re-apply on the next visit and undo the user's intent.
   */
  clearActiveLayout(): void {
    this.persistForActiveDashboard(null);
    this.savedLayoutIdForActive = null;
    this.activeLayoutIdSubject.next(null);
    this.pendingLayoutSubject.next(false);
  }

  /**
   * Wipe every saved layout: per-dashboard entries AND the legacy global
   * fallback. Used by Theme Options → "Reset to Default" so the user gets
   * a true clean slate across all dashboards (not just the one they're on).
   */
  clearAllSavedLayouts(): void {
    this.perDashboardSaved = {};
    this.legacyGlobalLayoutId = null;
    try {
      localStorage.removeItem(STORAGE_KEY_PER_DASHBOARD);
      localStorage.removeItem(STORAGE_KEY_LEGACY_GLOBAL);
    } catch {
      /* ignore */
    }
    this.savedLayoutIdForActive = null;
    this.activeLayoutIdSubject.next(null);
    this.pendingLayoutSubject.next(false);
  }

  private resolveLayoutIdFor(
    key: string | null,
    dashboardDefault: string | null,
  ): string | null {
    // Explicit per-dashboard pick (including an explicit null = "no layout
    // for this dashboard") overrides every other source. The `in` check is
    // intentional: a stored null means "user cleared this", which must not
    // fall through to the JSON default or legacy global.
    if (key && key in this.perDashboardSaved) {
      return this.perDashboardSaved[key];
    }
    if (dashboardDefault) return dashboardDefault;
    return this.legacyGlobalLayoutId;
  }

  private persistForActiveDashboard(id: string | null): void {
    if (!this.activeDashboardKey) return;
    this.perDashboardSaved[this.activeDashboardKey] = id;
    this.persistPerDashboardMap();
  }

  private persistPerDashboardMap(): void {
    try {
      const keys = Object.keys(this.perDashboardSaved);
      if (!keys.length) {
        localStorage.removeItem(STORAGE_KEY_PER_DASHBOARD);
      } else {
        localStorage.setItem(
          STORAGE_KEY_PER_DASHBOARD,
          JSON.stringify(this.perDashboardSaved),
        );
      }
    } catch {
      /* ignore */
    }
  }

  private readPerDashboardMap(): Record<string, string | null> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PER_DASHBOARD);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      const out: Record<string, string | null> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (v === null) out[k] = null;
        else if (typeof v === 'string' && v) out[k] = v;
      }
      return out;
    } catch {
      return {};
    }
  }

  private readLegacyGlobal(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY_LEGACY_GLOBAL);
    } catch {
      return null;
    }
  }

  /**
   * Reshape a dashboard pageContent into the rows specified by the given
   * layout preset. Widgets are taken from `pageContent` in document order;
   * the first `sum(rowCounts)` widgets are packed into the layout pattern,
   * then any leftover widgets continue in rows sized to the layout's
   * *last* row count — so the dashboard ends with the same visual rhythm
   * as the layout's final row (e.g. [2,3,1] with 9 widgets ends in three
   * col-12 rows; [3,3,3] with 10 widgets ends with a row of 1). The very
   * last row may be short if widgets don't divide evenly. If no layout
   * matches, the original pageContent is returned unchanged.
   *
   * Bootstrap col classes are computed per-row from the row's actual width
   * so widgets fill the row evenly (1 → col-12, 2 → col-md-6, 3 → col-md-4,
   * 4 → col-md-3, fall back to a percentage-based width otherwise).
   */
  reshapePageContent(pageContent: any[] | null | undefined, layoutId: string | null): any[] {
    const safeOriginal = Array.isArray(pageContent) ? pageContent : [];
    const layout = this.getLayout(layoutId);
    if (!layout) return safeOriginal;

    const widgets: any[] = [];
    for (const row of safeOriginal) {
      const cols = Array.isArray(row?.columns) ? row.columns : [];
      for (const col of cols) widgets.push(col);
    }
    if (!widgets.length) return safeOriginal;

    // Drop zero/negative entries so they can't consume slots silently.
    const validRowCounts = layout.rowCounts.filter(
      (n) => Number.isFinite(n) && n > 0,
    );
    if (!validRowCounts.length) return safeOriginal;

    const rows: any[] = [];
    let cursor = 0;
    const pushRow = (count: number, idSuffix: string) => {
      const actualSize = Math.min(count, widgets.length - cursor);
      const sized = widgets.slice(cursor, cursor + actualSize).map((w) => ({
        ...w,
        class: this.colClassForRowSize(actualSize),
      }));
      rows.push({ id: `dvm-layout-${layout.id}-${idSuffix}`, columns: sized });
      cursor += actualSize;
    };

    // Cycle the layout's rowCounts pattern until every widget is placed —
    // e.g. layout [3, 5] with 16 widgets produces rows of 3, 5, 3, 5. Without
    // cycling, anything past the first pattern pass falls off (or, with a
    // last-row-only repeat, the rhythm breaks: 3, 5, 5, 5, …). The final row
    // may be shorter than its pattern slot if widgets run out — pushRow's
    // actualSize handles the truncation.
    let patternIdx = 0;
    let rowOrdinal = 1;
    while (cursor < widgets.length) {
      const count = validRowCounts[patternIdx % validRowCounts.length];
      pushRow(count, `row${rowOrdinal}`);
      patternIdx++;
      rowOrdinal++;
    }

    return rows.length ? rows : safeOriginal;
  }

  private colClassForRowSize(size: number): string {
    if (size <= 1) return 'col-12';
    if (size === 2) return 'col-12 col-md-6';
    if (size === 3) return 'col-12 col-md-6 col-lg-4';
    if (size === 4) return 'col-12 col-md-6 col-lg-3';
    if (size === 6) return 'col-12 col-md-4 col-lg-2';
    // Fallback: equal share via Bootstrap's responsive cols, capped to 12.
    const span = Math.max(1, Math.floor(12 / size));
    return `col-12 col-md-${span}`;
  }
}
